import React from 'react';
import BaseChart from './BaseChart';

/**
 * CostComparisonChart Component
 *
 * Displays cost comparison between GPT-4 and GPT-3.5 using a bar chart with:
 * - Average cost per transaction comparison
 * - Side-by-side bars
 * - Interactive tooltips
 * - Responsive design
 */
const CostComparisonChart = ({ data, period, loading = false }) => {
  if (
    loading ||
    !data ||
    !data.cost_comparison ||
    data.cost_comparison.length === 0
  ) {
    return (
      <BaseChart
        title={`Cost Comparison (${period || 'N/A'})`}
        subtitle="Average cost per transaction: GPT-4 vs GPT-3.5"
        type="bar"
        series={[]}
        height={400}
        loading={loading}
      />
    );
  }

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 400,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        barHeight: '70%',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
        columnWidth: '60%',
      },
    },
    colors: ['#1976d2', '#4caf50'],
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val ? '$' + val.toFixed(4) : '$0.0000';
      },
      style: {
        fontSize: '11px',
        fontWeight: 'bold',
        colors: ['#333'],
      },
      offsetY: -20,
    },
    xaxis: {
      categories: data.cost_comparison.map(model => model.model_name),
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
      title: {
        text: 'Models',
        style: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '11px',
        },
        formatter: function (val) {
          return val ? '$' + val.toFixed(4) : '$0.0000';
        },
      },
      title: {
        text: 'Average Cost per Transaction',
        style: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const model = data.cost_comparison[dataPointIndex];
        if (!model) {
          return '<div>No data available</div>';
        }

        return `
          <div style="padding: 12px; background: white; border: 1px solid #e0e0e0; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); min-width: 280px;">
            <div style="font-weight: bold; margin-bottom: 8px; color: #1976d2; font-size: 14px;">${model.model_name}</div>
            <div style="margin-bottom: 4px;">Avg Cost per Transaction: <strong>$${model.avg_cost_per_execution.toFixed(4)}</strong></div>
            <div style="margin-bottom: 4px;">Total Executions: <strong>${model.executions.toLocaleString()}</strong></div>
            <div style="margin-bottom: 4px;">Total Cost: <strong>$${model.total_cost.toFixed(4)}</strong></div>
            <div style="margin-bottom: 4px;">Success Rate: <strong>${model.success_rate.toFixed(1)}%</strong></div>
            <div style="margin-bottom: 4px;">Avg Execution Time: <strong>${model.avg_execution_time.toFixed(2)}s</strong></div>
          </div>
        `;
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
      padding: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 20,
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 350,
          },
          dataLabels: {
            style: {
              fontSize: '9px',
            },
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '10px',
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '9px',
              },
            },
          },
        },
      },
    ],
  };

  const series = [
    {
      name: 'Average Cost per Transaction',
      data: data.cost_comparison.map(model => model.avg_cost_per_execution),
    },
  ];

  return (
    <BaseChart
      title={`Cost Comparison (${period || 'N/A'})`}
      subtitle="Average cost per transaction: GPT-4 vs GPT-3.5"
      type="bar"
      series={series}
      options={chartOptions}
      height={400}
      loading={loading}
    />
  );
};

export default CostComparisonChart;
